import { oc } from "@orpc/contract";
import { z } from "zod";
import { AR_AGING_BASES } from "@erp/shared-types";
import { InboundEmailSchema } from "../accounts-payable/ap-invoices.contract";

/**
 * AR Invoices RPC Contract
 *
 * Provides endpoints for listing invoices with filtering/pagination and fetching
 * invoice detail including line items, shipments, and payment history.
 */

const DecimalStringSchema = z.string().regex(/^-?\d+(\.\d+)?$/, {
  message: "Must be a numeric string",
});

const UUIDSchema = z.uuid();

/**
 * Invoice status enum
 */
export const InvoiceStatusSchema = z.enum([
  "draft",
  "open",
  "closed",
  "voided",
]);

export const agingBasisSchema = z.enum(AR_AGING_BASES);

export type AgingBasis = z.infer<typeof agingBasisSchema>;

/**
 * Pagination schema
 */
export const ArInvoicePaginationSchema = z.object({
  total: z.int().nonnegative(),
  limit: z.int().positive(),
  offset: z.int().nonnegative(),
});

/**
 * List AR invoices input schema
 */
export const ListArInvoicesInputSchema = z.object({
  customerId: z.array(UUIDSchema).optional(),
  status: z.array(InvoiceStatusSchema).optional(),
  invoiceDateFrom: z.string().optional(),
  invoiceDateTo: z.string().optional(),
  dueDateFrom: z.string().optional(),
  dueDateTo: z.string().optional(),
  /** Inclusive lower bound for firstSentAt (date-only, YYYY-MM-DD). */
  firstSentAtFrom: z.iso.date().optional(),
  /** Inclusive upper bound for firstSentAt (date-only, YYYY-MM-DD). */
  firstSentAtTo: z.iso.date().optional(),
  /**
   * "any" or omitted: all invoices.
   * "sent": only invoices with at least one invoice_sent event.
   * "unsent": only invoices with zero invoice_sent events.
   */
  sentState: z.enum(["sent", "unsent"]).optional(),
  overdue: z.boolean().optional(),
  netAmountMin: z.number().optional(),
  netAmountMax: z.number().optional(),
  balanceDueMin: z.number().optional(),
  balanceDueMax: z.number().optional(),
  appliedAmountMin: z.number().optional(),
  appliedAmountMax: z.number().optional(),
  search: z.string().optional(),
  sortBy: z
    .enum([
      "invoiceDate",
      "dueDate",
      "netAmount",
      "balanceDue",
      "customerName",
      "firstSentAt",
    ])
    .optional(),
  sortDirection: z.enum(["asc", "desc"]).optional(),
  limit: z.int().min(1).max(100).optional(),
  offset: z.int().min(0).optional(),
  agingBasis: agingBasisSchema.optional(),
  agingDateFrom: z.string().date().optional(),
  agingDateTo: z.string().date().optional(),
});

/**
 * A single "invoice was sent to the customer" event, sourced from
 * ar_invoice_events (event_type = 'invoice_sent'). Includes both the
 * in-app email path (ArInvoiceEmailService) and the Famous workflow
 * delivery path (ingestSendEvents).
 */
export const ArInvoiceSendEventSchema = z.object({
  /** ISO timestamp of when the send fired (ar_invoice_events.occurred_at). */
  occurredAt: z.iso.datetime(),
  /** Human-readable summary, e.g. "Invoice emailed to ap@customer.com". */
  note: z.string().nullable(),
});

/**
 * AR invoice list item schema — minimal fields for the list view
 */
export const ArInvoiceListItemSchema = z.object({
  id: UUIDSchema,
  invoiceNumber: z.string().nullable(),
  invoiceDate: z.string().nullable(),
  dueDate: z.string().nullable(),
  status: InvoiceStatusSchema,
  grossAmount: DecimalStringSchema.nullable(),
  netAmount: DecimalStringSchema.nullable(),
  balanceDue: DecimalStringSchema.nullable(),
  currencyCode: z.string().nullable(),
  customerName: z.string().nullable(),
  /**
   * The customer's name as the source ERP renders it (Famous shows
   * `Acme Foods (ACM)`). Display-only — `customerName` stays the raw value so
   * sorting, filtering, and export are unaffected.
   */
  erpDisplayName: z.string().nullable(),
  customerId: UUIDSchema,
  customerPurchaseOrderReference: z.string().nullable(),
  salesOrderId: UUIDSchema,
  shipDate: z.string().nullable(),
  paymentApplicationCount: z.int().nonnegative(),
  /**
   * Earliest send timestamp across all send-events for the invoice, or null
   * when the invoice has never been sent. Convenience field — same as
   * sendEvents[0].occurredAt when sendEvents is non-empty.
   */
  firstSentAt: z.iso.datetime().nullable(),
  /**
   * Every "Invoice Sent" event for this invoice, ordered oldest → newest.
   * Empty array when the invoice has never been sent.
   */
  sendEvents: z.array(ArInvoiceSendEventSchema),
});

/**
 * List AR invoices response schema
 */
export const ListArInvoicesResponseSchema = z.object({
  invoices: z.array(ArInvoiceListItemSchema),
  pagination: ArInvoicePaginationSchema,
});

/**
 * Get AR invoice input schema
 */
export const GetArInvoiceInputSchema = z.object({
  id: UUIDSchema,
});

/**
 * AR invoice line item schema (from SalesOrderLineItem)
 */
export const ArInvoiceLineItemSchema = z.object({
  id: UUIDSchema,
  lineItemNumber: z.number().nullable(),
  quantity: z.number().nullable(),
  unitPrice: DecimalStringSchema.nullable(),
  lineTotal: DecimalStringSchema,
  productName: z.string().nullable(),
  productSku: z.string().nullable(),
});

/**
 * AR invoice shipment schema (from ArShipment)
 */
export const ArInvoiceShipmentSchema = z.object({
  id: UUIDSchema,
  shipDate: z.string().nullable(),
  deliveryDate: z.string().nullable(),
  carrier: z.string().nullable(),
  trackingReference: z.string().nullable(),
  status: z.string(),
  quantity: z.number().nullable(),
  weight: DecimalStringSchema.nullable(),
});

/**
 * AR invoice sales order schema
 */
export const ArInvoiceSalesOrderSchema = z.object({
  id: UUIDSchema,
  customerPurchaseOrderNumber: z.string().nullable(),
  orderDate: z.string(),
  shipDate: z.string().nullable(),
  lineItems: z.array(ArInvoiceLineItemSchema),
  shipments: z.array(ArInvoiceShipmentSchema),
});

/**
 * Invoice-level line item schema (from ar_invoice_line_items)
 *
 * Distinct from ArInvoiceLineItemSchema which represents sales order line items.
 * This schema covers product lines, charges, and credit memos unified into one table.
 */
export const InvoiceLineItemDetailSchema = z.object({
  id: UUIDSchema,
  lineType: z.enum([
    "product",
    "freight",
    "tax",
    "misc_charge",
    "adjustment",
    "credit_memo",
    "company_absorbed",
  ]),
  description: z.string().nullable(),
  quantity: DecimalStringSchema.nullable(),
  rate: DecimalStringSchema.nullable(),
  amount: DecimalStringSchema,
  costBearer: z.enum(["customer", "company"]),
});

/**
 * AR invoice payment schema
 */
export const ArInvoicePaymentSchema = z.object({
  id: UUIDSchema,
  referenceNumber: z.string().nullable(),
  paymentDate: z.string().nullable(),
  paymentAmount: DecimalStringSchema.nullable(),
  paymentMethod: z.string().nullable(),
});

/**
 * AR invoice payment application schema
 */
export const ArInvoicePaymentApplicationSchema = z.object({
  id: UUIDSchema,
  appliedAmount: DecimalStringSchema.nullable(),
  applicationDate: z.string().nullable(),
  payment: ArInvoicePaymentSchema.nullable(),
});

/**
 * AR invoice event schema (from ar_invoice_events)
 */
export const InvoiceEventSchema = z.object({
  id: UUIDSchema,
  occurredAt: z.string(),
  actorType: z.enum(["user", "system", "integration"]),
  eventType: z.string(),
  amount: DecimalStringSchema.nullable(),
  note: z.string().nullable(),
  payload: z.record(z.string(), z.unknown()).nullable(),
});

/**
 * Outbound email summary for invoice detail (sent invoice emails)
 */
export const ArOutboundEmailSchema = z.object({
  id: UUIDSchema,
  subject: z.string(),
  from: z.string(),
  to: z.string(),
  cc: z.string().nullable(),
  sentAt: z.string().nullable(),
  status: z.string(),
});

/**
 * Inbound email on the AR invoice detail. Extends the shared shape with a
 * `pendingRemittance` flag set when the link to this invoice comes through an
 * unapproved `ar_remittance_line_item` (i.e. a customer-claimed match that
 * hasn't yet materialized an `ar_payment_applications` row).
 */
export const ArInvoiceInboundEmailSchema = InboundEmailSchema.extend({
  pendingRemittance: z.boolean(),
});

/**
 * The salesperson assigned to this invoice, resolved server-side from
 * `Invoice.salesPersonContactId` (preferred — the per-load override) and
 * falling back to `Customer.primarySalesPersonContactId` when the load
 * doesn't carry one. Null when Famous never assigned a rep at either level.
 */
export const ArInvoiceSalespersonSchema = z.object({
  contactId: UUIDSchema,
  name: z.string(),
  email: z.string().nullable(),
});

/**
 * AR invoice detail schema — full invoice with all nested associations
 */
export const ArInvoiceDetailSchema = ArInvoiceListItemSchema.extend({
  customerCode: z.string().nullable(),
  netAmount: DecimalStringSchema.nullable(),
  totalPaid: DecimalStringSchema.nullable(),
  paymentTermDays: z.number().nullable(),
  paymentTermsDescription: z.string().nullable(),
  isSent: z.boolean(),
  originSystem: z.string().nullable(),
  originId: z.string().nullable(),
  originEntity: z.string().nullable(),
  salesOrder: ArInvoiceSalesOrderSchema.nullable(),
  salesperson: ArInvoiceSalespersonSchema.nullable(),
  invoiceLineItems: z.array(InvoiceLineItemDetailSchema),
  invoiceEvents: z.array(InvoiceEventSchema),
  paymentApplications: z.array(ArInvoicePaymentApplicationSchema),
  inboundEmails: z.array(ArInvoiceInboundEmailSchema),
  outboundEmails: z.array(ArOutboundEmailSchema),
});

/**
 * Get AR invoice response schema
 */
export const GetArInvoiceResponseSchema = z.object({
  invoice: ArInvoiceDetailSchema.nullable(),
});

/**
 * Refresh AR invoice from Famous input/output schemas
 */
export const RefreshArInvoiceFromFamousInputSchema = z.object({
  id: UUIDSchema,
});

export const RefreshArInvoiceFromFamousResponseSchema = z.object({
  success: z.boolean(),
  invoice: ArInvoiceDetailSchema.nullable(),
});

/**
 * Bulk header-only refresh — for the page-open hot path (e.g. opening a
 * report). Refreshes only mutable header fields (status, amounts, balance,
 * due date, payment terms, customer PO ref) on the listed invoices.
 *
 * The backend chunks Oracle round-trips at 500/batch internally; the 5000 cap
 * here is a sanity bound on payload size.
 */
export const RefreshArInvoiceHeadersFromFamousInputSchema = z.object({
  ids: z.array(UUIDSchema).min(1).max(5000),
});

export const RefreshArInvoiceHeadersFromFamousResponseSchema = z.object({
  refreshed: z.int().nonnegative(),
  skipped: z.int().nonnegative(),
  failed: z.int().nonnegative(),
});

/**
 * Header refresh scoped by customer rather than explicit invoice ids. The
 * server resolves every open invoice for the given customers and refreshes
 * them (chunked internally), so callers never ship a large id array — used by
 * the bulk statement dialog's on-open refresh. Same best-effort response shape.
 */
export const RefreshArInvoiceHeadersForCustomersFromFamousInputSchema =
  z.object({
    customerIds: z.array(UUIDSchema).min(1).max(2000),
  });

/**
 * AR Invoices RPC Contract
 */
export const arInvoicesContracts = {
  list: oc
    .input(ListArInvoicesInputSchema)
    .output(ListArInvoicesResponseSchema)
    .route({ method: "GET" }),
  get: oc
    .input(GetArInvoiceInputSchema)
    .output(GetArInvoiceResponseSchema)
    .route({ method: "GET" }),
  refreshFromFamous: oc
    .input(RefreshArInvoiceFromFamousInputSchema)
    .output(RefreshArInvoiceFromFamousResponseSchema),
  refreshHeadersFromFamous: oc
    .input(RefreshArInvoiceHeadersFromFamousInputSchema)
    .output(RefreshArInvoiceHeadersFromFamousResponseSchema),
  refreshHeadersForCustomersFromFamous: oc
    .input(RefreshArInvoiceHeadersForCustomersFromFamousInputSchema)
    .output(RefreshArInvoiceHeadersFromFamousResponseSchema),
} as const;

// Type exports
export type InvoiceStatus = z.infer<typeof InvoiceStatusSchema>;
export type ArInvoicePagination = z.infer<typeof ArInvoicePaginationSchema>;
export type ListArInvoicesInput = z.infer<typeof ListArInvoicesInputSchema>;
export type ListArInvoicesResponse = z.infer<
  typeof ListArInvoicesResponseSchema
>;
export type ArInvoiceListItem = z.infer<typeof ArInvoiceListItemSchema>;
export type ArInvoiceSendEvent = z.infer<typeof ArInvoiceSendEventSchema>;
export type GetArInvoiceInput = z.infer<typeof GetArInvoiceInputSchema>;
export type GetArInvoiceResponse = z.infer<typeof GetArInvoiceResponseSchema>;
export type ArInvoiceDetail = z.infer<typeof ArInvoiceDetailSchema>;
export type ArInvoiceInboundEmail = z.infer<typeof ArInvoiceInboundEmailSchema>;
export type ArInvoiceLineItem = z.infer<typeof ArInvoiceLineItemSchema>;
export type ArInvoiceShipment = z.infer<typeof ArInvoiceShipmentSchema>;
export type ArInvoiceSalesOrder = z.infer<typeof ArInvoiceSalesOrderSchema>;
export type ArInvoiceSalesperson = z.infer<typeof ArInvoiceSalespersonSchema>;
export type ArInvoicePayment = z.infer<typeof ArInvoicePaymentSchema>;
export type ArInvoicePaymentApplication = z.infer<
  typeof ArInvoicePaymentApplicationSchema
>;
export type InvoiceLineItemDetail = z.infer<typeof InvoiceLineItemDetailSchema>;
export type InvoiceEvent = z.infer<typeof InvoiceEventSchema>;
export type ArOutboundEmail = z.infer<typeof ArOutboundEmailSchema>;
export type RefreshArInvoiceFromFamousInput = z.infer<
  typeof RefreshArInvoiceFromFamousInputSchema
>;
export type RefreshArInvoiceFromFamousResponse = z.infer<
  typeof RefreshArInvoiceFromFamousResponseSchema
>;
export type RefreshArInvoiceHeadersFromFamousInput = z.infer<
  typeof RefreshArInvoiceHeadersFromFamousInputSchema
>;
export type RefreshArInvoiceHeadersFromFamousResponse = z.infer<
  typeof RefreshArInvoiceHeadersFromFamousResponseSchema
>;
export type RefreshArInvoiceHeadersForCustomersFromFamousInput = z.infer<
  typeof RefreshArInvoiceHeadersForCustomersFromFamousInputSchema
>;
